import React, { useState } from 'react';
import { useUserRoles } from '../../contexts/UserRolesContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { useAuth } from '../../contexts/AuthContext';
import { addOffice, updateOffice } from '../../services/officeService';
import { RoleService } from '../../services/roleService';
import { getCurrentMillis } from '../../utils/dateUtils';
import { Building2, MapPin, Phone, Mail, Globe, Users, Crown, Shield, Calendar, ChevronRight, ChevronLeft, Plus, RefreshCw, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import AddOfficeModal from '../../components/modals/AddOfficeModal';
import EditOfficeModal from '../../components/modals/EditOfficeModal';
import { SectionHeader } from '../../components/ui';
import type { Office, Role } from '../../types';

const MyOffices: React.FC = () => {
  const { offices, loading, error, getRolesByOfficeId, refreshData } = useUserRoles();
  const { strings, language, isRTL } = useLocalization();
  const { getSubSpecialityName, getLawyerTitleName } = useSystemPreset();
  const { user } = useAuth();
  const [selectedOffice, setSelectedOffice] = useState<string | null>(null);
  const [isAddOfficeModalOpen, setIsAddOfficeModalOpen] = useState(false);
  const [isCreatingOffice, setIsCreatingOffice] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);
  const [isEditOfficeModalOpen, setIsEditOfficeModalOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState<Office | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isUpdatingOffice, setIsUpdatingOffice] = useState(false);

  const handleOfficeSelect = (officeId: string) => {
    setSelectedOffice(selectedOffice === officeId ? null : officeId);
  };

  const handleRefresh = () => {
    refreshData();
  };

  const exportToExcel = () => {
    try {
      const exportData = offices.map(office => ({
        'Office ID': office.id,
        'Office Name': office.name,
        'Address': office.address || 'N/A',
        'Phone': office.phone || 'N/A',
        'Email': office.email || 'N/A',
        'Website': office.website || 'N/A',
        'Sub Speciality': 'N/A', // subSpecialityID property doesn't exist in Office type
        'Created At': office.createdAt ? new Date(parseInt(office.createdAt)).toLocaleDateString() : 'N/A',
        'Updated At': office.updatedAt ? new Date(parseInt(office.updatedAt)).toLocaleDateString() : 'N/A',
        'Number of Roles': office.members ? office.members.length : 0,
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'Offices');
      XLSX.writeFile(wb, `offices_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting offices to Excel:', err);
      setCreationError(language === 'ar' ? 'فشل في تصدير المكاتب' : 'Failed to export offices to Excel.');
    }
  };

  const handleAddOffice = async (officeData: any, roleData: any) => {
    if (!user) {
      setCreationError('User not authenticated');
      return;
    }

    try {
      setIsCreatingOffice(true);
      setCreationError(null);

      console.log('🏢 Creating office with data:', officeData);
      console.log('👤 Creating role with data:', roleData);
      console.log('🔐 User authentication status:', {
        userId: user.id,
        userEmail: user.email,
        isActive: user.isActive,
        isLawyer: user.isLawyer
      });

      // Verify Firebase Auth state
      const { auth } = await import('../../config/firebase');
      const currentUser = auth.currentUser;
      console.log('🔐 Firebase Auth current user:', {
        uid: currentUser?.uid,
        email: currentUser?.email,
        emailVerified: currentUser?.emailVerified
      });

      if (!currentUser) {
        throw new Error('Firebase authentication not found. Please log in again.');
      }

      // Create the office
      const officeToCreate: Omit<Office, 'id'> = {
        name: officeData.name,
        alternativeName: officeData.alternativeName || '',
        address: officeData.address || '',
        phoneNumber: officeData.phoneNumber || '',
        email: officeData.email || '',
        website: officeData.website || '',
        description: officeData.description || '',
        logoURL: officeData.logoURL || '',
        isActive: true,
        isDeleted: false,
        isArchived: false,
        createdAt: getCurrentMillis().toString(),
        updatedAt: getCurrentMillis().toString(),
        createdBy: user.id,
        updatedBy: user.id,
        creatorID: user.id,
        stuffMembersList: [] // Array of user IDs - will be updated after role creation
      };

      // Create office in Firestore
      console.log('📝 Attempting to create office in Firestore...');
      const officeId = await addOffice(officeToCreate);
      console.log('✅ Office created with ID:', officeId);

      // Create the role for the current user
      const roleToCreate: Omit<Role, 'id'> = {
        officeID: officeId,
        officeName: officeData.name,
        officeLogoURL: officeData.logoURL || '',
        officePhone: officeData.phoneNumber || '',
        lawyerID: user.id,
        lawyerName: user.name || user.email || strings.myOffices.unknown,
        lawyerPhotoURL: user.photoURL || '',
        isOfficeCreator: roleData.isOwner, // Map isOwner to isOfficeCreator
        isOfficeAdmin: roleData.isOwner, // Owner is also admin
        subSpecialityID: roleData.subSpecialty || '',
        lawyerTitleID: roleData.lawyerTitle || '', // Use lawyer title from modal
        salaryIndicator: roleData.salaryType || 0, // Use salary type from modal
        salaryValue: roleData.salaryValue || 0, // Use salary value from modal
        profitPercentage: roleData.profitPercentage || 0, // Use profit percentage from modal
        isArchived: false,
        isDeleted: false,
        clientsReferences: [],
        userPermission: {
          id: '',
          showClientsList: true,
          updateClientProfile: true,
          removeClientFromOffice: true,
          showOfficeList: true,
          updateOfficeProfile: true,
          removeOfficeFromOffice: true,
          showEvents: true,
          showEventsList: true,
          updateEvent: true,
          removeEvent: true,
          showReports: true,
          showReportsList: true,
          updateReport: true,
          removeReport: true,
          showClientFilesList: true,
          updateClientFile: true,
          removeClientFile: true,
          showSessionsList: true,
          updateSession: true,
          deleteSession: true,
          showLawyersList: true,
          updateLawyerProfile: true,
          removeLawyerFromOffice: true
        },
        createdAt: getCurrentMillis().toString(),
        updatedAt: getCurrentMillis().toString(),
        createdBy: user.id,
        updatedBy: user.id
      };

      // Create role in Firestore
      console.log('📝 Attempting to create role in Firestore...');
      const roleId = await RoleService.createUserRole(user.id, roleToCreate);
      console.log('✅ Role created with ID:', roleId);

      // Update office with the new member (stuffMembersList should be array of user IDs)
      console.log('📝 Attempting to update office with new member...');
      await updateOffice(officeId, {
        stuffMembersList: [user.id], // Array of user IDs
        updatedAt: getCurrentMillis().toString(),
        updatedBy: user.id
      });
      console.log('✅ Office updated with new member');

      // Refresh the cache to get updated data
      console.log('🔄 Refreshing offices cache...');
      await refreshData?.();

      console.log('🎉 Office and role created successfully!');
      setIsAddOfficeModalOpen(false);
      
    } catch (error) {
      console.error('❌ Error creating office:', error);
      
      // Handle specific Firebase permission errors
      if (error instanceof Error) {
        if (error.message.includes('permission') || error.message.includes('insufficient')) {
          setCreationError('Permission denied. Please make sure you are logged in and have the necessary permissions to create offices.');
        } else if (error.message.includes('unauthenticated')) {
          setCreationError('You are not authenticated. Please log in again.');
        } else {
          setCreationError(error.message);
        }
      } else {
        setCreationError('Failed to create office. Please try again.');
      }
    } finally {
      setIsCreatingOffice(false);
    }
  };

  const handleEditOffice = (office: Office) => {
    const officeRoles = getRolesByOfficeId(office.id);
    const userRole = officeRoles.find(role => role.lawyerID === user?.id);
    
    if (!userRole) {
      alert('You do not have a role in this office');
      return;
    }

    // Check if user is the only staff member
    const isOnlyStaffMember = officeRoles.length === 1;
    
    if (!isOnlyStaffMember) {
      alert('You can only edit your role information when you are the only staff member in this office');
      return;
    }

    setEditingOffice(office);
    setEditingRole(userRole);
    setIsEditOfficeModalOpen(true);
  };

  const handleUpdateOffice = async (updatedOffice: Office) => {
    try {
      setIsUpdatingOffice(true);
      // Refresh the cache to get updated data
      await refreshData?.();
    } catch (error) {
      console.error('Error updating office:', error);
    } finally {
      setIsUpdatingOffice(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">{strings.common.loading}</h2>
          <p className="text-slate-600 dark:text-slate-300">{strings.myOffices.noOffices}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">{strings.common.error}</h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary text-white rounded-2xl hover:bg-primary-dark transition-colors duration-300"
          >
            {strings.common.refresh}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={strings.myOffices.title}
          description={strings.myOffices.selectOffice}
          language={language}
          isRTL={isRTL}
          actionButtons={[
            {
              label: language === 'ar' ? 'تصدير إكسل' : 'Export Excel',
              onClick: exportToExcel,
              icon: <Download className="w-4 h-4" />,
              variant: 'secondary',
              requiresProfessional: false
            }
          ]}
          refreshButton={{
            onClick: handleRefresh,
            disabled: loading,
            loading: loading
          }}
          addButton={{
            label: strings.myOffices.addOffice,
            onClick: () => setIsAddOfficeModalOpen(true)
          }}
        >
          {/* Error Message */}
          {creationError && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-red-800 dark:text-red-200 text-sm">{creationError}</p>
              <button
                onClick={() => setCreationError(null)}
                className="mt-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 text-sm underline"
              >
                Dismiss
              </button>
            </div>
          )}
        </SectionHeader>

        {/* Two Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Office List */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                  {strings.myOffices.title}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {offices.length} office{offices.length !== 1 ? 's' : ''} found
                </p>
              </div>
              
              <div className="p-4">
                {offices.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Building2 className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">
                      {strings.myOffices.noOffices}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {offices.map((office) => {
                      const officeRoles = getRolesByOfficeId(office.id);
                      const isSelected = selectedOffice === office.id;
                      // Get the current user's role in this office
                      const userRole = officeRoles.find(role => role.lawyerID === user?.id);
                      // Get the role name (lawyer title) for display
                      const roleName = userRole?.lawyerTitleID 
                        ? getLawyerTitleName(userRole.lawyerTitleID, language as 'en' | 'ar')
                        : (officeRoles.length > 0 && officeRoles[0].lawyerTitleID
                          ? getLawyerTitleName(officeRoles[0].lawyerTitleID, language as 'en' | 'ar')
                          : strings.myOffices.legalProfessional);
                      
                      return (
                        <div
                          key={office.id}
                          className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                            isSelected 
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-primary shadow-sm' 
                              : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                          }`}
                          onClick={() => handleOfficeSelect(office.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {office.logoURL ? (
                                  <img 
                                    src={office.logoURL} 
                                    alt={office.name}
                                    className="w-10 h-10 rounded-lg object-cover"
                                  />
                                ) : (
                                  <Building2 className="w-5 h-5" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-slate-800 dark:text-white text-sm truncate">
                                  {office.name}
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {roleName}
                                </p>
                              </div>
                            </div>
                            {isRTL ? (
                              <ChevronLeft className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                                isSelected ? 'rotate-90' : ''
                              }`} />
                            ) : (
                              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                                isSelected ? 'rotate-90' : ''
                              }`} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Office Details */}
          <div className="lg:col-span-2">
            {selectedOffice ? (
              (() => {
                const office = offices.find(o => o.id === selectedOffice);
                const officeRoles = getRolesByOfficeId(selectedOffice);
                
                if (!office) return null;
                
                return (
                  <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
                    {/* Office Header */}
                    <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                      <div className="flex items-start space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                          {office.logoURL ? (
                            <img 
                              src={office.logoURL} 
                              alt={office.name}
                              className="w-16 h-16 rounded-2xl object-cover"
                            />
                          ) : (
                            <Building2 className="w-8 h-8" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">
                            {office.name}
                          </h2>
                          {office.alternativeName && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">
                              {office.alternativeName}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
                            <span className="flex items-center space-x-1">
                              <Users className="w-4 h-4" />
                              <span>{officeRoles.length} {officeRoles.length !== 1 ? strings.myOffices.rolesPlural : strings.myOffices.roles}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{strings.myOffices.joined} {new Date(office.createdAt).toLocaleDateString()}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Office Details */}
                    <div className="p-6 space-y-6">
                      {/* Contact Information */}
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                          {strings.myOffices.officeInformation}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {office.address && (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{strings.myOffices.officeAddress}</p>
                              </div>
                              <p className="text-slate-800 dark:text-white text-sm pl-6">{office.address}</p>
                            </div>
                          )}
                          
                          {office.phoneNumber && (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Phone className="w-4 h-4 text-slate-400" />
                                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{strings.myOffices.officePhone}</p>
                              </div>
                              <p className="text-slate-800 dark:text-white text-sm pl-6">{office.phoneNumber}</p>
                            </div>
                          )}
                          
                          {office.email && (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Mail className="w-4 h-4 text-slate-400" />
                                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{strings.myOffices.officeEmail}</p>
                              </div>
                              <p className="text-slate-800 dark:text-white text-sm pl-6">{office.email}</p>
                            </div>
                          )}
                          
                          {office.website && (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Globe className="w-4 h-4 text-slate-400" />
                                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{strings.myOffices.officeWebsite}</p>
                              </div>
                              <a 
                                href={office.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 text-sm hover:underline pl-6 block"
                              >
                                {office.website}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Your Roles Section */}
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                          {strings.myOffices.yourRoles}
                        </h3>
                        <div className="space-y-4">
                          {officeRoles.map((role) => (
                            <div
                              key={role.id}
                              className="bg-slate-50 dark:bg-slate-700 rounded-xl p-5 border border-slate-200 dark:border-slate-600"
                            >
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                  <h4 className="text-lg font-semibold text-slate-800 dark:text-white">
                                    {role.lawyerTitleID ? (() => {
                                      const titleName = getLawyerTitleName(role.lawyerTitleID, language as 'en' | 'ar');
                                      console.log('🔍 Debug - MyOffices: Lawyer title display', {
                                        lawyerTitleID: role.lawyerTitleID,
                                        language,
                                        titleName
                                      });
                                      return titleName;
                                    })() : strings.myOffices.legalProfessional}
                                  </h4>
                                  {role.isOfficeCreator && (
                                    <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs font-medium rounded-full flex items-center space-x-1">
                                      <Crown className="w-3 h-3" />
                                      <span>{strings.myOffices.creator}</span>
                                    </span>
                                  )}
                                  {role.isOfficeCreator && (
                                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full flex items-center space-x-1">
                                      <Shield className="w-3 h-3" />
                                      <span>{strings.myOffices.admin}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                                <div className="space-y-2">
                                  <p className="text-slate-600 dark:text-slate-400 font-medium">{strings.myOffices.salaryType}</p>
                                  <p className="text-slate-800 dark:text-white">
                                    {role.salaryIndicator === 0 && strings.myOffices.studentIntern}
                                    {role.salaryIndicator === 1 && strings.myOffices.fixedSalary}
                                    {role.salaryIndicator === 2 && strings.myOffices.percentageBased}
                                    {role.salaryIndicator === 3 && strings.myOffices.fixedPercentage}
                                  </p>
                                </div>
                                
                                {role.salaryValue > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-slate-600 dark:text-slate-400 font-medium">{strings.myOffices.salaryValue}</p>
                                    <p className="text-slate-800 dark:text-white">
                                      {role.salaryValue} {role.salaryIndicator === 2 || role.salaryIndicator === 3 ? '%' : 'EGP'}
                                    </p>
                                  </div>
                                )}
                                
                                {role.profitPercentage > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-slate-600 dark:text-slate-400 font-medium">{strings.myOffices.profitPercentage}</p>
                                    <p className="text-slate-800 dark:text-white">
                                      {role.profitPercentage}%
                                    </p>
                                  </div>
                                )}
                                
                                <div className="space-y-2">
                                  <p className="text-slate-600 dark:text-slate-400 font-medium">{strings.myOffices.specialty}</p>
                                  <p className="text-slate-800 dark:text-white">
                                    {role.subSpecialityID ? (() => {
                                      const specialityName = getSubSpecialityName(role.subSpecialityID, language as 'en' | 'ar');
                                      console.log('🔍 Debug - MyOffices: Speciality display', {
                                        subSpecialityID: role.subSpecialityID,
                                        language,
                                        specialityName
                                      });
                                      return specialityName;
                                    })() : strings.myOffices.generalPractice}
                                  </p>
                                </div>
                                
                                <div className="space-y-2">
                                  <p className="text-slate-600 dark:text-slate-400 font-medium">{strings.myOffices.status}</p>
                                  <p className="text-slate-800 dark:text-white">
                                    {role.isArchived ? strings.myOffices.archived : strings.myOffices.active}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Edit Office Button */}
                      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                              {strings.myOffices.actions || 'Actions'}
                            </h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {(() => {
                                const officeRoles = getRolesByOfficeId(office.id);
                                const isOnlyStaffMember = officeRoles.length === 1;
                                return isOnlyStaffMember 
                                  ? strings.myOffices.canEditRole || 'You can edit your role information'
                                  : strings.myOffices.cannotEditRole || 'You can only edit when you are the only staff member';
                              })()}
                            </p>
                          </div>
                          <button
                            onClick={() => handleEditOffice(office)}
                            disabled={(() => {
                              const officeRoles = getRolesByOfficeId(office.id);
                              return officeRoles.length !== 1;
                            })()}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              (() => {
                                const officeRoles = getRolesByOfficeId(office.id);
                                const isOnlyStaffMember = officeRoles.length === 1;
                                return isOnlyStaffMember
                                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                  : 'bg-slate-300 dark:bg-slate-600 text-slate-500 dark:text-slate-400 cursor-not-allowed';
                              })()
                            }`}
                          >
                            {strings.myOffices.editOffice || 'Edit Office'}
                          </button>
                        </div>
                      </div>
                    </div>

                  </div>
                );
              })()
            ) : (
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
                    {strings.myOffices.selectOfficeTitle}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-500">
                    {strings.myOffices.selectOfficeDescription}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Office Modal */}
      <AddOfficeModal
        isOpen={isAddOfficeModalOpen}
        onClose={() => {
          setIsAddOfficeModalOpen(false);
          setCreationError(null);
        }}
        onSave={handleAddOffice}
        isLoading={isCreatingOffice}
      />

      {/* Edit Office Modal */}
      <EditOfficeModal
        isOpen={isEditOfficeModalOpen}
        onClose={() => {
          setIsEditOfficeModalOpen(false);
          setEditingOffice(null);
          setEditingRole(null);
        }}
        onSave={handleUpdateOffice}
        office={editingOffice}
        role={editingRole}
        isLoading={isUpdatingOffice}
      />
    </div>
  );
};

export default MyOffices;
